import React, { useEffect, useState } from 'react';
import { Loader2, Eye, MousePointer, Download, Mail, ExternalLink, Users } from 'lucide-react';
import { ShareDemoAnalyticsSummary } from '../types';
import { getShareDemoAnalytics } from '../services/supabase';

interface ShareDemoAnalyticsProps {
  demoId: string;
}

export default function ShareDemoAnalytics({ demoId }: ShareDemoAnalyticsProps) {
  const [data, setData] = useState<ShareDemoAnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const analytics = await getShareDemoAnalytics(demoId);
      setData(analytics);
      setLoading(false);
    })();
  }, [demoId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 size={20} className="text-slate-400 animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const stats = [
    { label: 'Views', value: data.views, icon: Eye, color: 'text-blue-400' },
    { label: 'Unique Visitors', value: data.unique_visitors, icon: Users, color: 'text-cyan-400' },
    { label: 'Slider Uses', value: data.slider_interactions, icon: MousePointer, color: 'text-purple-400' },
    { label: 'Download Clicks', value: data.download_clicks, icon: Download, color: 'text-green-400' },
    { label: 'Downloads', value: data.download_completes, icon: Download, color: 'text-emerald-400' },
    { label: 'Emails Captured', value: data.emails_captured, icon: Mail, color: 'text-orange-400' },
    { label: 'CTA Clicks', value: data.cta_clicks, icon: ExternalLink, color: 'text-amber-400' },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {stats.map((s) => (
          <div key={s.label} className="bg-slate-900 border border-slate-800 rounded-xl p-3 text-center">
            <s.icon size={16} className={`mx-auto mb-1 ${s.color}`} />
            <div className="text-lg font-bold text-white">{s.value}</div>
            <div className="text-xs text-slate-500">{s.label}</div>
          </div>
        ))}
      </div>

      {data.recent_leads.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-white mb-2">Captured Leads</h4>
          <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800 text-slate-400">
                  <th className="text-left px-4 py-2 font-medium">Email</th>
                  <th className="text-left px-4 py-2 font-medium">Name</th>
                  <th className="text-left px-4 py-2 font-medium">Agency</th>
                  <th className="text-left px-4 py-2 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_leads.map((lead, i) => (
                  <tr key={i} className="border-b border-slate-800/50 last:border-0">
                    <td className="px-4 py-2 text-white">{lead.email}</td>
                    <td className="px-4 py-2 text-slate-400">{lead.first_name || '—'}</td>
                    <td className="px-4 py-2 text-slate-400">{lead.agency_name || '—'}</td>
                    <td className="px-4 py-2 text-slate-500 text-xs">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
